import { logger } from "./logger";

/**
 * Custom error class for expected errors that should be logged to DataDog
 * and display appropriate user-facing messages.
 */
export class ExpectedError extends Error {
  public readonly context?: Record<string, string | number | boolean>;

  constructor(
    message: string,
    context?: Record<string, string | number | boolean>,
  ) {
    super(message);
    this.name = "ExpectedError";
    this.context = context;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ExpectedError);
    }
  }
}

/**
 * Type guard to check if an error is an ExpectedError
 */
export function isExpectedError(error: unknown): error is ExpectedError {
  return error instanceof ExpectedError;
}

/**
 * Creates an ExpectedError for data loading failures
 */
export function createDataLoadingError(
  resourceName: string,
  processId?: string,
): ExpectedError {
  return new ExpectedError(
    `${resourceName} not found. Please refresh the page.`,
    {
      resourceName,
      processId: processId || "unknown",
      errorType: "data_loading_error",
    },
  );
}

/**
 * Handles uncaught errors that occur outside any ErrorBoundary.
 * These errors would otherwise be unhandled and not logged.
 */
export const handleUncaughtError = (
  error: unknown,
  errorInfo: { componentStack?: string | undefined },
) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const context: Record<string, string | number | boolean> = {
    errorType: "unexpected_error",
    errorName: errorObj.name,
    errorSource: "onUncaughtError",
  };

  if (errorInfo.componentStack) {
    context.componentStack = errorInfo.componentStack;
  }

  if (isExpectedError(error)) {
    logger.error(
      `Uncaught expected error: ${errorObj.message}`,
      {
        ...context,
        errorType: error.context?.errorType || "expected_error",
        ...error.context,
      },
      errorObj,
    );
  } else {
    logger.error(
      `Uncaught unexpected error: ${errorObj.message}`,
      context,
      errorObj,
    );
  }
};

/**
 * Handles errors that React catches in ErrorBoundaries.
 * These are errors that were caught but still need to be logged.
 */
export const handleCaughtError = (
  error: unknown,
  errorInfo: {
    componentStack?: string | undefined;
    errorBoundary?: object | undefined;
  },
) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const context: Record<string, string | number | boolean> = {
    errorType: "unexpected_error",
    errorName: errorObj.name,
    errorSource: "onCaughtError",
  };

  if (errorInfo.componentStack) {
    context.componentStack = errorInfo.componentStack;
  }

  if (isExpectedError(error)) {
    logger.error(
      `Caught expected error in ErrorBoundary: ${errorObj.message}`,
      {
        ...context,
        errorType: error.context?.errorType || "expected_error",
        ...error.context,
      },
      errorObj,
    );
  } else {
    logger.error(
      `Caught unexpected error in ErrorBoundary: ${errorObj.message}`,
      context,
      errorObj,
    );
  }
};

/**
 * Handles errors that React automatically recovers from.
 * These are typically hydration mismatches or other recoverable issues.
 */
export const handleRecoverableError = (
  error: unknown,
  errorInfo: { componentStack?: string | undefined },
) => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const context: Record<string, string | number | boolean> = {
    errorType: "unexpected_error",
    errorName: errorObj.name,
    errorSource: "onRecoverableError",
  };

  if (errorInfo.componentStack) {
    context.componentStack = errorInfo.componentStack;
  }

  if (isExpectedError(error)) {
    logger.warn(`Recoverable expected error: ${errorObj.message}`, {
      ...context,
      errorType: error.context?.errorType || "expected_error",
      ...error.context,
    });
  } else {
    logger.warn(`Recoverable unexpected error: ${errorObj.message}`, context);
  }
};
